import type { ReactElement } from "react";
import { useEffect, useId, useState } from "react";
import { Button, Input, Label } from "@/shared/components/ui";
import { isRegistrationFinancialAuthRejection } from "./registration-financial-client";
import { useRegistrationFinancialClient } from "./use-registration-financial";

/**
 * Optional "email a copy of my receipt to" input (FR-044 — legacy "Send Copy
 * of Invoice/Receipt To" port). Rendered ONLY inside an offered payment step.
 * Set/clear go exclusively to the dedicated session-owned financial-service
 * endpoint — never bundled into the Stripe confirm call — and the value is
 * pre-populated from the endpoint's read-back. Client-side validation is
 * advisory only: the server is authoritative, and a rejection renders a
 * user-safe message on the input without ever blocking payment confirmation.
 */

const RECEIPT_COPY_MESSAGES = {
  saved: "Receipt copy address saved.",
  cleared: "Receipt copy address cleared.",
  invalid_address:
    "That address couldn't be saved. Enter one valid email address, or leave it blank.",
  no_longer_editable: "The receipt copy address can no longer be changed.",
  unavailable: "The receipt copy address couldn't be updated right now. You can still pay.",
} as const;

type ReceiptCopyMessageKey = keyof typeof RECEIPT_COPY_MESSAGES;

/** Advisory-only shape hint; never blocks submission (server is authoritative). */
function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

export function RegistrationReceiptCopyField({
  handoffId,
  onUnauthorized,
}: {
  handoffId: string;
  /** FR-046: a 401 on any receipt-copy call reverts the whole flow. */
  onUnauthorized?: () => void;
}): ReactElement {
  const client = useRegistrationFinancialClient();
  const inputId = useId();
  const messageId = useId();
  const [email, setEmail] = useState("");
  const [persistedEmail, setPersistedEmail] = useState<string | null>(null);
  const [message, setMessage] = useState<ReceiptCopyMessageKey | null>(null);
  const [advisoryHint, setAdvisoryHint] = useState(false);
  const [busy, setBusy] = useState(false);

  // Pre-populate from the token-authorized read-back. A failed read leaves
  // the input empty and never blocks the payment step; a 401 reverts the
  // flow (FR-046).
  useEffect(() => {
    let cancelled = false;
    client
      .getReceiptCopy(handoffId)
      .then((preference) => {
        if (!cancelled && preference.email !== null) {
          setEmail(preference.email);
          setPersistedEmail(preference.email);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled && isRegistrationFinancialAuthRejection(error)) {
          onUnauthorized?.();
        }
      });
    return () => {
      cancelled = true;
    };
  }, [client, handoffId, onUnauthorized]);

  const save = async (): Promise<void> => {
    const candidate = email.trim();
    if (busy || candidate.length === 0) {
      return;
    }
    setAdvisoryHint(!looksLikeEmail(candidate));
    setBusy(true);
    setMessage(null);
    try {
      const result = await client.setReceiptCopy(handoffId, candidate);
      if (result.outcome === "saved") {
        setPersistedEmail(result.preference.email);
        if (result.preference.email !== null) {
          setEmail(result.preference.email);
        }
        setAdvisoryHint(false);
        setMessage("saved");
      } else {
        setMessage(result.reason);
      }
    } catch (error) {
      if (isRegistrationFinancialAuthRejection(error)) {
        onUnauthorized?.();
      } else {
        setMessage("unavailable");
      }
    } finally {
      setBusy(false);
    }
  };

  const clear = async (): Promise<void> => {
    if (busy) {
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      const result = await client.clearReceiptCopy(handoffId);
      if (result.outcome === "cleared") {
        setPersistedEmail(null);
        setEmail("");
        setAdvisoryHint(false);
        setMessage("cleared");
      } else {
        setMessage(result.reason);
      }
    } catch (error) {
      if (isRegistrationFinancialAuthRejection(error)) {
        onUnauthorized?.();
      } else {
        setMessage("unavailable");
      }
    } finally {
      setBusy(false);
    }
  };

  const invalid = message === "invalid_address";
  const rejection =
    message === "invalid_address" || message === "no_longer_editable" || message === "unavailable";

  return (
    <div className="mt-6 rounded-md border border-clm-light-gray bg-off-white/50 p-4">
      <Label htmlFor={inputId}>Email a copy of my receipt to (optional)</Label>
      <div className="mt-2 flex flex-col gap-2 sm:flex-row">
        <Input
          id={inputId}
          type="email"
          inputMode="email"
          autoComplete="email"
          maxLength={254}
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            // New input invalidates the last attempt's feedback — both the
            // server message and the latched advisory format hint.
            setMessage(null);
            setAdvisoryHint(false);
          }}
          aria-invalid={invalid || undefined}
          aria-describedby={messageId}
          placeholder="name@example.com"
          className="sm:max-w-sm"
        />
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => void save()}
            disabled={busy || email.trim().length === 0}
          >
            Save
          </Button>
          {persistedEmail !== null && (
            <Button type="button" variant="outline" onClick={() => void clear()} disabled={busy}>
              Clear
            </Button>
          )}
        </div>
      </div>
      {/* Live region: save/clear/rejection feedback is announced without
          moving focus, and a rejection never blocks payment confirmation.
          Rejections render alert-red (DES-1); confirmations stay gray. */}
      <p
        id={messageId}
        role="status"
        className={`mt-2 text-sm ${rejection ? "text-alert-red" : "text-gray-600"}`}
      >
        {message !== null
          ? RECEIPT_COPY_MESSAGES[message]
          : advisoryHint
            ? "This doesn't look like an email address. It will only be saved if it is valid."
            : null}
      </p>
    </div>
  );
}
